import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AlertTriangle, Image as ImageIcon, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { ticketsApi } from '../../lib/api';

const MAX_FILE_MB = 5;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

/**
 * "Fehler melden" – Modal für Teilnehmer und Admins, um Bugs direkt aus der App
 * zu melden. Erstellt ein Ticket im Admin-Ticket-System (POST /api/tickets/report)
 * mit optionalem Screenshot (Bild bis 5 MB). Ort und Browser werden automatisch
 * ergänzt, damit Admins den Kontext des Fehlers nachvollziehen können.
 */
export function ReportIssueDialog({ open, onOpenChange }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setTitle('');
            setDescription('');
            setFile(null);
            setPreview(null);
            setSubmitting(false);
        }
    }, [open]);

    // Build preview URL for selected image
    useEffect(() => {
        if (!file) {
            setPreview(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const captureContext = () => {
        let urlPath = '';
        try { urlPath = window.location.pathname + window.location.search; } catch { /* ignore */ }
        let ua = '';
        try { ua = navigator.userAgent || ''; } catch { /* ignore */ }
        return { url_path: urlPath, user_agent: ua };
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!ALLOWED_TYPES.includes(f.type)) {
            toast.error('Nur Bilddateien (PNG, JPG, GIF, WEBP) sind erlaubt');
            e.target.value = '';
            return;
        }
        if (f.size > MAX_FILE_MB * 1024 * 1024) {
            toast.error(`Datei zu groß (max. ${MAX_FILE_MB} MB)`);
            e.target.value = '';
            return;
        }
        setFile(f);
    };

    const handleRemoveFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const t = title.trim();
        const d = description.trim();
        if (t.length < 3 || t.length > 200) {
            toast.error('Bitte einen aussagekräftigen Titel (3–200 Zeichen) angeben');
            return;
        }
        if (d.length < 3 || d.length > 5000) {
            toast.error('Bitte eine Beschreibung (3–5000 Zeichen) angeben');
            return;
        }
        setSubmitting(true);
        try {
            const ctx = captureContext();
            const fd = new FormData();
            fd.append('title', t);
            fd.append('description', d);
            fd.append('url_path', ctx.url_path);
            fd.append('user_agent', ctx.user_agent);
            if (file) fd.append('screenshot', file);
            await ticketsApi.report(fd);
            toast.success('Vielen Dank – Ihre Meldung wurde übermittelt.');
            onOpenChange(false);
        } catch (err) {
            const msg = err?.response?.data?.detail;
            toast.error(typeof msg === 'string' ? msg : 'Meldung konnte nicht gesendet werden');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="report-issue-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Fehler melden
                    </DialogTitle>
                    <DialogDescription>
                        Sie sind auf ein Problem gestoßen? Kurz beschreiben, optional einen Screenshot anhängen –
                        die Administratoren werden umgehend informiert.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="report-title">Titel *</Label>
                        <Input
                            id="report-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="z. B. Login-Button reagiert nicht"
                            maxLength={200}
                            required
                            data-testid="report-issue-title"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="report-description">Was ist passiert? *</Label>
                        <Textarea
                            id="report-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={5}
                            placeholder="Bitte beschreiben Sie das Problem so genau wie möglich – Schritte, erwartetes Verhalten, tatsächliches Verhalten."
                            maxLength={5000}
                            required
                            data-testid="report-issue-description"
                        />
                        <p className="text-xs text-muted-foreground">
                            {description.length} / 5000 Zeichen
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="report-screenshot" className="flex items-center gap-1.5">
                            <ImageIcon className="h-4 w-4" />
                            Screenshot (optional, max. {MAX_FILE_MB} MB)
                        </Label>
                        {!file ? (
                            <Input
                                id="report-screenshot"
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                onChange={handleFileChange}
                                data-testid="report-issue-screenshot"
                            />
                        ) : (
                            <div className="border rounded-md p-2 flex items-start gap-3 bg-muted/30" data-testid="report-issue-screenshot-preview">
                                {preview && (
                                    <img
                                        src={preview}
                                        alt="Vorschau"
                                        className="h-20 w-20 object-cover rounded border shrink-0"
                                    />
                                )}
                                <div className="flex-1 min-w-0 text-xs">
                                    <p className="font-medium truncate">{file.name}</p>
                                    <p className="text-muted-foreground">
                                        {(file.size / 1024).toFixed(1)} KB · {file.type}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleRemoveFile}
                                    className="text-muted-foreground hover:text-red-600 transition-colors p-1"
                                    aria-label="Screenshot entfernen"
                                    data-testid="report-issue-screenshot-remove"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2 border">
                        Zur besseren Nachverfolgung senden wir automatisch die aktuelle Seite und Browser-Info mit.
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                            data-testid="report-issue-cancel"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="report-issue-submit"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4 mr-1.5" />
                            )}
                            Meldung senden
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default ReportIssueDialog;
